
import { useStore } from "@/store";
import { defineComponent, computed, ref, inject, onMounted } from "vue";
import swal from "sweetalert2";
import { useRouter } from "vue-router";
import moment from "moment";
import { api } from "@/services/Api";
import { useI18n } from "vue-i18n";
import BasketItems from "@/components/BasketItems.vue";
import { Customer, NotAvailableProducts } from "@/models/Order";
import q from "q";
import useOrder from "@/modules/useOrder";
import _ from "lodash";
import { PrintSize } from "@/models/Enums";
import { Emitter } from "mitt";
import { VIRTUAL_SLOT_ID } from '@/models/Constants';
import mixin from "@/mixin";

export default defineComponent({
  name: "ReservedOrderView",
  components: {
    BasketItems,
  },
  async setup() {
    const emitter = inject("emitter") as Emitter<any>;
    const { printTicket } = useOrder();
    const router = useRouter();
    const { t } = useI18n();
    const store = useStore();
    const canEditDiscount = computed(() => store.getters.canEditDiscount);
    const amountDiscount = computed(() =>
      store.state.order.products.reduce((accumulator, object) => {
        return accumulator + object.priceLine.discount;
      }, 0)
    );
    const originOrder = computed(() => store.state.originOrder);
    const isSave = computed(() => router.currentRoute.value.meta?.save);
    const orderId = computed(() => store.state.order.id);
    const products = computed(() => store.state.order.products);
    const order = computed(() => store.state.order);
    const total = computed(() =>
      products.value.reduce((acc, curr) => {
        return (acc +=
          (curr.priceLine?.priceForOne ?? 0) * (curr.priceLine?.quantity ?? 0) -
          (curr.priceLine?.discount ?? 0));
      }, 0)
    );
    const totalWithOutDiscount = computed(() =>
      products.value.reduce((acc, curr) => {
        return (acc +=
          (curr.priceLine?.priceForOne ?? 0) * (curr.priceLine?.quantity ?? 0));
      }, 0)
    );
    const notAvailableProducts = ref<NotAvailableProducts[]>([]);

    async function checkOut() {
      if (store.state.order.products.length == 0) {
        return;
      }

      const price = store.state.priceTypes.find(
        (x) => x.id == store.state.order.priceType
      );
      if (!price) {
        return;
      }

      var isEqual = mixin.methods.orderChanged(store.state.order.products, originOrder.value.products);
      // We skip validating the order if it has not been changed
      if (!isEqual) {
        const order = _.cloneDeep(store.state.order);
        order.voucherCode = store.state.scanValue;
        order.products.forEach((orderProduct) => {
          orderProduct.startDateTime =
            orderProduct.date + "T" + orderProduct.slotStartTime;
        });
        const validationResult = await api
          .withLoader()
          .ValidateOrder(order, null);
        if (validationResult?.errorMessage) {
          swal.fire({
            icon: "error",
            title: t("error-pop-up.oops"),
            text: validationResult.errorMessage,
          });
          return;
        }
        if (!validationResult?.data) {
          swal.close();
          return;
        }
      }
      store.commit("setEditCustomerMode", false);
      router.push({ name: "selectPayment" });
    }

    async function SaveOrder() {
      swal.showLoading();
      notAvailableProducts.value = [];
      const order = store.state.order;
      const budgets = store.state.budgets;
      order.products.forEach((orderProduct) => {
        orderProduct.startDateTime =
          orderProduct.date + "T" + orderProduct.slotStartTime;
      });
      const responsePromise = await api.SaveOrder(order, budgets);
      await q.delay(400);
      const response = await responsePromise;
      if (response.errorMessage) {
        swal.fire({
          icon: "error",
          title: t("error-pop-up.oops"),
          text: response.errorMessage,
        });
        return false;
      }
      if (!response.data) {
        swal.close();
        return false;
      }
      if (response.data.notAvailableProducts.length > 0) {
        notAvailableProducts.value = response.data.notAvailableProducts;
        swal.fire({
          icon: "warning",
          text: t("alert.exceeded-quantity"),
          customClass: {
            confirmButton: "save-button-wrapper popup-bookit-button my-1 px-4",
          },
          buttonsStyling: false,
        });
        return false;
      }
      await swal.fire({
        icon: "success",
        text: t("cashier.alert.order-saved"),
        confirmButtonText: t("cashier.label.ok"),
      });

      return true;
    }

    function pay() {
      router.push({ name: "selectPayment" });
    }

    function getDisplayDate(d: string) {
      return moment(d).format("YYYY-MM-DD");
    }

    const isToPayment = computed(() => {
      return (
        router.currentRoute.value.name == "customerDetails" ||
        router.currentRoute.value.name == "groupsCustomerDetails"
      );
    });

    const currencySign = computed(() => {
      return store.state.currencies.find(
        (x) => x.id == store.state.order.currencyId
      )?.symbol;
    });
    async function SaveOrderWithCustomer() {
      swal.showLoading();
      notAvailableProducts.value = [];
      const order = store.state.order;
      order.products.forEach((orderProduct) => {
        orderProduct.startDateTime =
          orderProduct.date + "T" + orderProduct.slotStartTime;
      });
      const responsePromise = api.SaveOrderWithCustomer(order);
      await q.delay(400);
      const response = await responsePromise;
      if (response.errorMessage) {
        swal.fire({
          icon: "error",
          title: t("error-pop-up.oops"),
          text: response.errorMessage,
        });
        return false;
      }
      if (!response.data) {
        swal.close();
        return false;
      }
      if (response.data.notAvailableProducts.length > 0) {
        notAvailableProducts.value = response.data.notAvailableProducts;
        swal.fire({
          icon: "warning",
          text: t("alert.exceeded-quantity"),
          customClass: {
            confirmButton: "save-button-wrapper popup-bookit-button my-1 px-4",
          },
          buttonsStyling: false,
        });
        return false;
      }
      swal.close();
      emitter.emit("reservedOrderSaved");
      return true;
    }
    function validateCustomer(customer: Customer) {
      if (!customer.firstName) {
        return "First name is Required";
      }
      if (!customer.lastName) {
        return "Last name is Required";
      }
      if (!customer.mobile) {
        return "Mobile is Required";
      }
      return null;
    }

    async function saveOrderAction() {
      if (store.state.order.products.length == 0) {
        return;
      }

      const price = store.state.priceTypes.find(
        (x) => x.id == store.state.order.priceType
      );
      if (!price) {
        return;
      }
      const obj1 = store.state.order.customer;
      const obj2 = store.state.reservedOrderCustomer;
      if (JSON.stringify(obj1) === JSON.stringify(obj2)) {
        await SaveOrder();
      } else {
        var error = validateCustomer(obj1);
        if (error) {
          swal.fire({
            icon: "error",
            title: t("error-pop-up.oops"),
            text: error,
          });
          return;
        }
        await SaveOrderWithCustomer();
      }
    }

    function addNewProduct() {
      router.push({ name: "select-products" });
    }
    async function printTicketAction() {
      printTicket(
        orderId.value,
        store.getters.printerSettings?.ticketPrinterSize ?? PrintSize.Check_8cm
      );
    }

    return {
      products,
      total,
      checkOut,
      getDisplayDate,
      isToPayment,
      pay,
      currencySign,
      saveOrderAction,
      canEditDiscount,
      orderId,
      isSave,
      addNewProduct,
      amountDiscount,
      order,
      totalWithOutDiscount,
      notAvailableProducts,
      printTicketAction,
    };
  },
});
